import { isPatientGroupingEnabled } from '@allurion/domain';
import { PatientHead, SearchIcon } from '@allurion/ui';
import { useIntl } from 'react-intl';

import { TrackedEmptyState } from 'src/analytics/TrackedUI';
import { ClinicSettings } from 'src/domain/Clinic';
import { useAppNavigate } from 'src/hooks/useAppNavigate';
import { useCurrentProvider } from 'src/hooks/useCurrentProvider';

import clinicPatientsMessages from './clinic-patients-messages';

type Props = {
  noAvailablePatients: boolean;
  emptySearch: boolean;
  settings: ClinicSettings;
  clinicId: string;
};

export function ClinicPatientsEmptyState({
  emptySearch,
  noAvailablePatients,
  settings,
  clinicId,
}: Props) {
  const intl = useIntl();
  const { toInvitePatientPage, toManagePatientsPage } = useAppNavigate();
  const { isAdmin } = useCurrentProvider();
  const title = emptySearch
    ? intl.formatMessage(clinicPatientsMessages.noPatientsFoundTitle)
    : intl.formatMessage(clinicPatientsMessages.noPatientsAvailableTitle);

  return (
    <TrackedEmptyState
      icon={emptySearch ? <SearchIcon /> : <PatientHead />}
      title={title}
      subtitle={getSubtitle()}
      buttons={getButtons()}
      trackedLabel={noAvailablePatients ? 'no-patients-available' : 'no-patients-found'}
    />
  );

  function getSubtitle() {
    if (emptySearch) {
      return intl.formatMessage(clinicPatientsMessages.noPatientsFoundSubtitle);
    }

    if (isAdmin) {
      return isPatientGroupingEnabled(settings)
        ? intl.formatMessage(
            clinicPatientsMessages.noPatientsAvailableForAdminWithPatientGroupingSubtitle
          )
        : intl.formatMessage(
            clinicPatientsMessages.noPatientsAvailableForAdminWithoutPatientGroupingSubtitle
          );
    }

    return isPatientGroupingEnabled(settings)
      ? intl.formatMessage(
          clinicPatientsMessages.noPatientsAvailableForStaffWithPatientGroupingSubtitle
        )
      : intl.formatMessage(
          clinicPatientsMessages.noPatientsAvailableForStaffWithoutPatientGroupingSubtitle
        );
  }

  function getButtons() {
    if (emptySearch || !isAdmin) {
      return;
    }

    const addPatientButton = {
      label: intl.formatMessage({
        id: 'invite-patient-button',
        defaultMessage: 'Add new patient',
      }),
      onClick() {
        toInvitePatientPage(clinicId);
      },
    };
    const organizePatientsButton = {
      label: intl.formatMessage({
        id: 'navmenu.organize-patients',
        defaultMessage: 'Organize Patients',
      }),
      onClick() {
        toManagePatientsPage(clinicId);
      },
      variant: 'secondary',
    };

    if (isPatientGroupingEnabled(settings)) {
      return [addPatientButton, organizePatientsButton];
    }

    return [addPatientButton];
  }
}
